// src/components/DashboardCards/RecentProjectsCard.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import styles from '../../pages/DashboardOverview.module.css';
import DraftImage from '../../assets/icons/draft_icon.png';

const RecentProjectsCard = ({ recentProjects, recentProjectsLoading }) => {
  const navigate = useNavigate();

  // Helper functions for recent projects
  const getCardTitle = (item) => {
    if (!item) return 'Untitled Generation';
    
    try {
      let title = '';
      
      if (item.is_draft || item.request_type === 'draft' || item.request_type === 'bulk_draft') {
        title = item.draft_name || 'Untitled Draft';
      } else {
        const product = item.product || {};
        
        if (product.project_name && product.project_name.trim()) {
          title = product.project_name;
        } else if (item.project_name && item.project_name.trim()) {
          title = item.project_name;
        } else {
          const output = item.claude_output || item.output || {};
          if (output.project_name && output.project_name.trim()) {
            title = output.project_name;
          } else if (output.title) {
            title = output.title;
          } else if (product.description) {
            title = product.description.substring(0, 50) + (product.description.length > 50 ? '...' : '');
          } else {
            title = `${product.brand_name || ''} ${product.model_number || ''}`.trim() || 'Untitled Generation';
          }
        }
      }
      
      return title;
    } catch (err) {
      return 'Error getting title';
    }
  };

  const getCardImage = (item) => {
    if (item.is_draft || item.request_type === 'draft' || item.request_type === 'bulk_draft') {
      return null;
    }
    
    const output = item.claude_output || item.output || {};
    
    if (Array.isArray(output) && output.length > 0) {
      const firstOutput = output[0];
      if (firstOutput.generated_images && firstOutput.generated_images.length > 0) {
        return firstOutput.generated_images[0];
      }
      if (firstOutput.infographic_urls && firstOutput.infographic_urls.length > 0) {
        return firstOutput.infographic_urls[0];
      }
    }
    
    if (output.generated_images && output.generated_images.length > 0) {
      return output.generated_images[0];
    }
    
    if (output.infographic_urls && output.infographic_urls.length > 0) {
      return output.infographic_urls[0];
    }
    
    return null;
  };

  const getBulkPreviewImage = (bulkProject) => {
    if (bulkProject.image_urls && bulkProject.image_urls.parent_skus && bulkProject.image_urls.parent_skus.length > 0) {
      const firstSku = bulkProject.image_urls.parent_skus[0];
      if (firstSku.sections && firstSku.sections.primary && firstSku.sections.primary.main) {
        return firstSku.sections.primary.main;
      }
    }
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const getProjectId = (item) => {
    return item.editor_folder_id || item._id || '';
  };

  const getListingMode = (item) => {
    if (!item) return '';
    
    try {
      // Check request_type for bulk projects (bulk_gen_new or bulk_gen_existing)
      if (item.request_type) {
        if (item.request_type === 'bulk_gen_new') {
          return 'New Listing';
        } else if (item.request_type === 'bulk_gen_existing') {
          return 'Existing Listing';
        }
      }
      
      // Try different possible locations for listing mode
      if (item.listing_mode && item.listing_mode.trim()) {
        return item.listing_mode.charAt(0).toUpperCase() + item.listing_mode.slice(1) + ' Listing';
      }
      
      const product = item.product || {};
      if (product.listing_mode && product.listing_mode.trim()) {
        return product.listing_mode.charAt(0).toUpperCase() + product.listing_mode.slice(1) + ' Listing';
      }
      
      const output = item.claude_output || item.output || {};
      if (output.listing_mode && output.listing_mode.trim()) {
        return output.listing_mode.charAt(0).toUpperCase() + output.listing_mode.slice(1) + ' Listing';
      }
      
      return '';
    } catch (err) {
      return '';
    }
  };

  return (
    <div className={styles.recentProjectsCard}>
      <div 
        className={styles.recentProjectsHeader}
        onClick={() => navigate('/dashboard/projects')}
        style={{ cursor: 'pointer' }}
      >
        <h3 className={styles.recentProjectsTitle}>Recent Projects</h3>
        <FaChevronRight className={styles.recentProjectsIcon} />
      </div>
      <div className={styles.recentProjectsSeparator}></div>
      <div className={styles.recentProjectsContent}>
        {recentProjectsLoading ? (
          <div className={styles.recentProjectsLoading}>Loading...</div>
        ) : recentProjects.length === 0 ? (
          <div className={styles.recentProjectsEmpty}>No projects yet</div>
        ) : (
          <div className={styles.recentProjectsGrid}>
            {recentProjects.map((project) => {
              const isDraft = project.is_draft || project.request_type === 'draft' || project.request_type === 'bulk_draft';
              const cardImage = getCardImage(project);
              const projectTitle = getCardTitle(project);
              const projectId = getProjectId(project);
              const listingMode = getListingMode(project);
              const projectDate = formatDate(project.created_at);

              if (project.projectType === 'bulk') {
                const bulkImage = getBulkPreviewImage(project);
                return (
                  <div 
                    key={project._id || project.id} 
                    className={styles.recentProjectCardBulk}
                    onClick={() => {
                      navigate('/dashboard/projects', { state: { bulkProjectId: project._id } });
                    }}
                  >
                    <div className={styles.recentProjectCardBulkLayer1}></div>
                    <div className={styles.recentProjectCardBulkLayer2}></div>
                    <div className={styles.recentProjectCardBulkMain}>
                      <div className={styles.recentProjectInner}>
                        <div className={styles.recentProjectImageContainer}>
                          {bulkImage ? (
                            <img src={bulkImage} alt={projectTitle} className={styles.recentProjectImageBulk} />
                          ) : (
                            <div className={styles.recentProjectPlaceholder}>No Image</div>
                          )}
                        </div>
                        <div className={styles.recentProjectDetails}>
                          <div className={styles.recentProjectDetailsTop}>
                            <div className={styles.recentProjectTitle}>{projectTitle}</div>
                            {listingMode && (
                              <div className={styles.recentProjectIdTag}>
                                <span>{listingMode}</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.recentProjectDate}>
                            <span>{projectDate}</span>
                          </div>
                        </div>
                      </div>
                      <div className={styles.recentProjectScoreBadgeBulk}>
                        <span>Bulk</span>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div 
                  key={project._id || project.id} 
                  className={styles.recentProjectCard}
                  onClick={() => {
                    if (project.request_type === 'bulk_draft') {
                      navigate('/dashboard/generate/bulk', {
                        state: {
                          loadBulkDraft: true,
                          draftData: project,
                          isNewListing: project.listing_mode !== 'existing'
                        }
                      });
                    } else if (isDraft) {
                      // For single drafts, navigate to single listing generator with draft data
                      navigate('/dashboard/generate/single', {
                        state: {
                          loadDraft: true,
                          draftData: project,
                          isNewListing: project.listing_mode !== 'existing'
                        }
                      });
                    } else if (project.projectType === 'single') {
                      navigate('/dashboard/projects', { state: { projectId: project.editor_folder_id || project._id } });
                    } else {
                      navigate('/dashboard/projects', { state: { bulkProjectId: project._id } });
                    }
                  }}
                >
                  <div className={styles.recentProjectInner}>
                    <div className={styles.recentProjectImageContainer}>
                      {isDraft ? (
                        <div className={styles.recentProjectDraftImage}>
                          <img src={DraftImage} alt="Draft" />
                        </div>
                      ) : cardImage ? (
                        <img src={cardImage} alt={projectTitle} className={styles.recentProjectImage} />
                      ) : (
                        <div className={styles.recentProjectPlaceholder}>No Image</div>
                      )}
                    </div>
                    <div className={styles.recentProjectDetails}>
                      <div className={styles.recentProjectDetailsTop}>
                        <div className={styles.recentProjectTitle}>{projectTitle}</div>
                        {listingMode && (
                          <div className={styles.recentProjectIdTag}>
                            <span>{listingMode}</span>
                          </div>
                        )}
                      </div>
                      <div className={styles.recentProjectDate}>
                        {isDraft && (
                          <>
                            <span className={styles.recentProjectDraftBullet}>•</span>
                            <span className={styles.recentProjectDraftText}>Draft</span>
                            <span className={styles.recentProjectDateSeparator}>|</span>
                          </>
                        )}
                        <span>{projectDate}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles.recentProjectScoreBadge}>
                    <span>
                      {isDraft ? 'Single' : project.projectType === 'single' ? 'Single' : 'Bulk'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentProjectsCard;

